import moment from 'moment';

import type {
    EditionFragment as Edition,
    EditionFragment_from as EditionFrom,
    EditionFragment_audience as Audience,
    EditionFragment_audience_fromSavedFilter as SavedFilter,
    Setting as SettingType,
    GetNutshellMarketingStats_nutshellMarketingStats as NutshellMarketingStats,
    EditionFragment_gateError as GateError,
    SmsEditionFragment_gateError as SmsGateError,
    SmsBroadcastFragment_edition_audience as SmsAudience,
} from 'nutshell-graphql-types';

import {MIN_AUDIENCE_SIZE} from './new-preview-data';
import {RECIPIENT_TYPE_SAVED_LIST, RECIPIENT_TYPE_AUDIENCE} from './new-recipients-selection-view';
import type {SelectedRecipients} from './new-recipients-selection-view';

export function getBusinessHoursStart() {
    const businessHoursStart = moment(
        NutshellFrontendSettings.businessHours.start,
        NutshellFrontendSettings.businessHours.format
    );
    if (businessHoursStart.isBefore(moment())) {
        businessHoursStart.add(1, 'day');
    }

    return businessHoursStart;
}

export function getIssueCount(edition: Edition, streetAddressSetting?: SettingType | null): number {
    let count = 0;
    if (
        // In our new prepare to send page flow, audience isn't selected until later
        !NutClientConfig.hasNewAudienceManagementNUT14721 &&
        // @ts-expect-error
        (!edition.audience || edition.audience.membershipStats.totalCount === 0)
    ) {
        count += 1;
    }

    if (!edition.emailTemplate || !edition.emailTemplate.subject) {
        count += 1;
    }

    if (!edition.hasUnsubscribeLink) {
        count += 1;
    }

    if (!edition.hasStreetAddress || !streetAddressSetting || !streetAddressSetting.value) {
        count += 1;
    }

    if (
        // In our new prepare to send page flow, audience isn't selected until later
        (!NutClientConfig.hasNewAudienceManagementNUT14721 && !edition.audience) ||
        !edition.from ||
        (edition.from && edition.from.status !== 'VERIFIED')
    ) {
        count += 1;
    }

    return count;
}

export function getAudienceError(
    selectedRecipients?: SelectedRecipients | null,
    nutshellMarketingStats?: NutshellMarketingStats | null
) {
    let audienceError = null;
    if (selectedRecipients && isOverEmailSendingLimit(selectedRecipients, nutshellMarketingStats)) {
        audienceError = `The selected ${selectedRecipients.type} will put you over your email sending limit for this month. Please upgrade your plan or use another ${selectedRecipients.type} to continue.`;
    }

    return audienceError;
}

function isOverEmailSendingLimit(
    selectedRecipients?: SelectedRecipients | null,
    nutshellMarketingStats?: NutshellMarketingStats | null
): boolean {
    const recipientSelection = selectedRecipients ? selectedRecipients.recipientSelection : null;

    let maxNumberOfEmailsPerMonth = null;
    let numberOfEmailsSentThisMonth = null;
    if (nutshellMarketingStats) {
        maxNumberOfEmailsPerMonth = nutshellMarketingStats.gate.maxNumberOfEmailsPerMonth;
        numberOfEmailsSentThisMonth = nutshellMarketingStats.usageStats.numberOfEmailsSentThisMonth;
    }

    // Explicitly check for null and type instead of relying on falsy values (0 is a valid number)
    if (
        recipientSelection &&
        typeof maxNumberOfEmailsPerMonth === 'number' &&
        typeof numberOfEmailsSentThisMonth === 'number'
    ) {
        return (
            recipientSelection.recipientCount + numberOfEmailsSentThisMonth >
            maxNumberOfEmailsPerMonth
        );
    }

    return false;
}

export function getSelectedRecipientsFromAudience(
    audience: Audience | SmsAudience,
    isSmsEdition?: boolean
): SelectedRecipients {
    const membershipStats = isSmsEdition ? audience.smsMembershipStats : audience.membershipStats;
    const recipientCount = membershipStats.subscribedCount;

    // Represents a saved list
    if (audience.fromSavedFilter) {
        const savedFilter: SavedFilter = audience.fromSavedFilter;

        return {
            type: RECIPIENT_TYPE_SAVED_LIST,
            recipientSelection: {
                id: savedFilter.id,
                name: savedFilter.name,
                htmlUrlPath: savedFilter.htmlUrlPath,
                filterString: savedFilter.filterString,
                entityType: savedFilter.entityType,
                recipientCount: recipientCount,
            },
        };
    }

    return {
        type: RECIPIENT_TYPE_AUDIENCE,
        recipientSelection: {
            id: audience.id,
            name: audience.name,
            htmlUrlPath: audience.htmlUrlPathForList,
            unsubscribedRate: audience.membershipStats.unsubscribedRate,
            recipientCount: recipientCount,
            membershipStats: audience.membershipStats,
            smsMembershipStats: audience.smsMembershipStats,
        },
    };
}

export type EditionSecurityInfo = {
    sesDkimVerified: boolean;
    nutshellDkimVerified: boolean;
    dmarcDmarcVerified: boolean;
    isAllowedToSend: boolean;
};

export function getEditionSecurityInfo(editionFrom: EditionFrom): EditionSecurityInfo {
    const sesDkimVerified = Boolean(
        editionFrom.fromDomain && editionFrom.fromDomain.sesDkimStatus === 'STATUS_VERIFIED'
    );
    const nutshellDkimVerified = Boolean(
        editionFrom.fromDomain &&
            editionFrom.fromDomain.refreshedNutshellDkimStatus === 'STATUS_VERIFIED'
    );
    const dmarcDmarcVerified = Boolean(
        editionFrom.fromDomain && editionFrom.fromDomain.refreshedDmarcStatus === 'STATUS_VERIFIED'
    );

    return {
        sesDkimVerified,
        nutshellDkimVerified,
        dmarcDmarcVerified,
        isAllowedToSend: true,
    };
}

export function getError(
    gateError: GateError | SmsGateError | null | undefined,
    audienceError: string | null | undefined,
    currentView: string,
    sendError?: string | null
): {
    errorMessage: string | null | undefined;
    shouldDisable: boolean;
    showUpgradeBanner: boolean;
} {
    let errorMessage = null;
    let shouldDisable = false;
    let showUpgradeBanner = false;
    if (gateError) {
        errorMessage = gateError.message;
        shouldDisable = true;
        showUpgradeBanner = true;
    } else if (audienceError && currentView === 'recipients-selection') {
        errorMessage = audienceError;
    } else if (sendError) {
        errorMessage = sendError;
    }

    return {errorMessage, shouldDisable, showUpgradeBanner};
}

export function shouldPrepareViewButtonsBeDisabled(
    issueCount: number,
    errorMessage: string | null | undefined,
    isEditionLoading: boolean,
    editionSecurityInfo?: EditionSecurityInfo | null
): boolean {
    return Boolean(
        issueCount !== 0 ||
            errorMessage ||
            isEditionLoading ||
            !editionSecurityInfo ||
            !editionSecurityInfo.isAllowedToSend
    );
}

export function isTooShortForABTesting(
    isABTesting: boolean,
    recipientCount: number | null | undefined,
    currentView: string
): boolean {
    return Boolean(
        isABTesting &&
            recipientCount &&
            recipientCount < MIN_AUDIENCE_SIZE &&
            currentView === 'review'
    );
}

export function shouldAudienceSelectionViewButtonsBeDisabled(
    isValidRecipientSelection: boolean,
    errorMessage?: string | null
) {
    return Boolean(!isValidRecipientSelection || errorMessage);
}

export function getAudienceUnsubscribedRate(selectedRecipients?: SelectedRecipients | null) {
    return selectedRecipients &&
        selectedRecipients.type === RECIPIENT_TYPE_AUDIENCE &&
        selectedRecipients.recipientSelection &&
        selectedRecipients.recipientSelection.unsubscribedRate
        ? selectedRecipients.recipientSelection.unsubscribedRate
        : undefined;
}
